

















































































































































import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import Highlight from "@/components/highlight/highlight";
import { Route } from "vue-router";
import { UserModule } from "@/store/modules/User";
import debounce from "lodash/debounce";

@Component({
    metaInfo() {
        return {
            title: this.$t("admin.groups") as string
        };
    },
    components: { Highlight }
})
export default class GroupsComponent extends Vue {
    groups: Group[] = [];
    isLoading = false;
    internalSearchQuery = "";
    totalGroups = 0;

    @Prop(Object)
    query!: Query;

    @Watch("query.searchQuery")
    @Watch("query.perPage")
    queryUpdated() {
        this.updateData();
    }

    get isInternalUser() {
        return UserModule.isInternalUser;
    }

    get externalOffBoarded() {
        return !UserModule.isInternalUser && !UserModule.onBoardedDate;
    }

    created() {
        this.internalSearchQuery = this.query.searchQuery;
        this.updateData();
    }

    beforeRouteUpdate(to: Route, from: Route, next: Function) {
        if (from.name === "admin-groups-create" || from.name === "admin-groups-edit") {
            this.updateData();
        }

        next();
    }

    getGroupsQuery({ perPage = 10, currentPage = 1, searchQuery = "" }) {
        return {
            query: `query($first: Int, $after: Int, $contains: String) {
                        userGroups(first: $first, after: $after, contains: $contains) {
                            totalCount,
                            items {
                                userGroupId,
                                userGroupName,
                                creditorName
                                businessType
                                country
                                onBoardedDate
                                creditorId
                                numUsers
                                hasAccess
                                hasAll
                                hasReports
                                hasRights
                            }
                        }
                    }`,
            variables: {
                first: perPage,
                after: ((currentPage * perPage) - perPage),
                contains: searchQuery
            }
        };
    }

    async updateData() {
        this.isLoading = true;
        const { data } = await this.$api.graphql(this.getGroupsQuery(this.query));
        this.groups = data.userGroups.items;
        this.totalGroups = data.userGroups.totalCount;
        this.internalSearchQuery = this.query.searchQuery;
        this.isLoading = false;
    }

    onSearch({ target }: { target: HTMLInputElement }) {
        if (target.value !== this.query.searchQuery) {
            this.query.searchQuery = target.value;
        }
    }

    onSearchInput = debounce((value: string) => this.updateSearchQuery(value), 200);

    updateSearchQuery(value: string|undefined) {
        if (value !== undefined) {
            this.internalSearchQuery = value;
            this.query.searchQuery = value;
            this.query.currentPage = 1;
        }
    }

    onPageChange(page: number): void {
        this.query.currentPage = page;
        this.updateData();
    }

    openEdit(groupId: string): void {
        this.$router.push({ name: "admin-groups-edit", params: { id: groupId }, query: this.$route.query });
    }

    openCreate(groupId?: string): void {
        this.$router.push({
            name: "admin-groups-create",
            ...groupId && { params: { id: groupId } },
            query: this.$route.query
        });
    }

    onDelete(groupId: number) {
        this.$buefy.dialog.confirm({
            title: this.$t("admin.deleteGroupTitle") as string,
            message: this.$t("admin.deleteGroupMessage") as string,
            confirmText: this.$t("admin.deleteGroupConfirm") as string,
            cancelText: this.$t("admin.cancel") as string,
            type: "is-danger",
            hasIcon: true,
            onConfirm: () => this.delete(groupId)
        });
    }

    async delete(groupId: number) {
        try {
            const { data } = await this.$api.graphql({
                query: `mutation($groupId: ID!) {
                    deleteGroup(groupId: $groupId) {
                        userGroupName
                    }
                }`,
                variables: { groupId }
            });

            const [group] = data.deleteGroup;

            this.$notifications.success({ message: this.$t("admin.deleteGroupSuccess", { name: group.userGroupName }) as string });

            this.updateData();
        } catch (error) {
            this.$notifications.error({ message: this.$t("admin.deleteGroupFail") as string });
            this.$apm.captureError(error as any);
        }
    }

    resetSearch() {
        this.internalSearchQuery = "";
        this.query.searchQuery = "";
        this.query.currentPage = 1;
    }

    cloneDisabled(group: Group) {
        if (this.isInternalUser ? !group.hasAccess : !group.hasAll) {
            return { state: true, text: this.$t('admin.groupMissingAccess') as string };
        }
        if (!group.onBoardedDate) {
            return { state: true, text: this.$t('admin.notOnBoarded') as string };
        }
        return { state: false, text: "" };
    }

    deleteDisabled(group: Group) {
        if (this.isInternalUser ? !group.hasAccess : !group.hasAll) {
            return { state: true, text: this.$t('admin.groupMissingAccess') as string };
        }
        if (!group.onBoardedDate) {
            return { state: true, text: this.$t('admin.notOnBoarded') as string };
        }
        if (group.numUsers) {
            return { state: true, text: this.$t('admin.cantDeleteGroup') as string };
        }
        return { state: false, text: "" };
    }
}

interface Group {
    businessType: string;
    country: string;
    creditorId: number;
    creditorName: string;
    numUsers: number;
    onBoardedDate: string;
    userGroupId: number;
    userGroupName: string;
    hasAccess: boolean;
    hasAll: boolean;
    hasReports: boolean;
    hasRights: boolean;
}

interface Query {
    perPage: number;
    currentPage: number;
    searchQuery: string;
}
